import { useEffect, useState } from 'react';
import {
  TEAM_ACCESS_REQUIRED, teamAccessActive, teamAccessApprove, teamAccessDeny, teamAccessEnd, teamAccessRequest, teamAccessRequests, teamAccessReview,
  type ApiClient, type ApiError, type TeamAccessActive, type TeamAccessRequest as TeamAccessRequestRow,
} from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { passkeyStepUp } from '../settings/stepUp';

/**
 * Break-glass for the Aithora team (decision 4), drawn as Console V6 draws it (bgLayer):
 * "Restricted · request access" where the server refuses the team a resident's record,
 * "Break-glass active" while a grant runs, and "Break-glass request" for the building's
 * Facility Admin to approve, deny or review. Every rule is the server's
 * (src/aithora/team-access.ts); these only ask and show.
 */

/** True when a read was refused for the team, so the request belongs on it. */
export function isTeamRestricted(e: ApiError | null | undefined): boolean {
  const b = (e as { body?: { code?: string } } | null | undefined)?.body;
  return !!b && b.code === TEAM_ACCESS_REQUIRED;
}

const HOURS = [1, 4, 8];
/** The design's chip and button base (bgLayer C). */
const C = 'display:inline-flex;align-items:center;height:34px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
const hm = (iso: string | number) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const LABEL = 'display:flex;flex-direction:column;gap:4px;font-size:16px;font-weight:600;color:#3C454F;';
const FIELD = 'height:38px;border-radius:8px;border:1px solid rgba(34,39,46,.24);background:#fff;font-family:inherit;font-size:14px;padding:0 10px;';

function Btn({ s, onClick, children, disabled }: { s: string; onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <a
      href="#"
      role="button"
      aria-disabled={disabled || undefined}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      style={sx(s)}
    >
      {children}
    </a>
  );
}

/** "Break-glass active": the dark banner while a grant runs (design bgActiveShow). */
export function TeamAccessBanner({ client, orgId, residentId, buildingName, residentName, onEnded }: { client: ApiClient; orgId: string | null; residentId: string | null; buildingName?: string | null; residentName?: string | null; onEnded?: () => void }) {
  const [active, setActive] = useState<TeamAccessActive | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!orgId) return;
    let live = true;
    teamAccessActive(client, { query: { organizationId: orgId, ...(residentId ? { residentId } : {}) } })
      .then((r) => { if (live) setActive(r.active); })
      .catch(() => { if (live) setActive(null); });
    return () => { live = false; };
  }, [client, orgId, residentId]);
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  if (!active) return null;
  const ends = Date.parse(active.endsAt);
  if (Number.isFinite(ends) && ends <= now) return null;
  const left = Number.isFinite(ends) ? Math.max(0, Math.round((ends - now) / 60000)) : 0;
  const bldg = buildingName || 'the building';
  const first = (active.name ?? 'You').split(' ')[0];
  const sub =
    (buildingName ? buildingName : 'This building') +
    (active.residentId ? ' · ' + (residentName || 'one resident') : ' · whole building') +
    ' · ' + left + ' min left · every record you open is logged for ' + bldg +
    (active.emergency ? ' · emergency: the building reviews it within 1 business day' : '');
  const end = async () => {
    setErr(null);
    try {
      await teamAccessEnd(client, { params: { id: active.id } });
      setActive(null);
      onEnded?.();
    } catch (e) {
      setErr((e as Error).message || 'Access did not end. Try again.');
    }
  };
  return (
    <div role="status" data-screen-label="Break-glass active" data-testid="team-access-banner" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;padding:10px 14px;margin-bottom:12px;border-radius:10px;background:#3A2A08;color:#fff;')}>
      <span style={sx('flex:0 0 auto;white-space:nowrap;font-size:14px;font-weight:800;letter-spacing:.1em;padding:3px 7px;border-radius:4px;background:#F2C94C;color:#3A2A08;')}>BREAK-GLASS</span>
      <span style={sx('flex:1 1 260px;min-width:0;font-size:14px;line-height:1.35;')}>
        <b>{`Break-glass: ${first}, ${active.reason}, ends ${Number.isFinite(ends) ? hm(ends) : ''}`}</b>
        <span style={sx('display:block;font-size:16px;opacity:.85;')}>{sub}</span>
        {err && <span role="alert" style={sx('display:block;font-size:19px;font-weight:700;margin-top:2px;')}>{err}</span>}
      </span>
      <Btn s="display:inline-flex;align-items:center;height:34px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#fff;color:#3A2A08;" onClick={() => void end()}>
        End access now
      </Btn>
    </div>
  );
}

/**
 * "Restricted · request access" (design bgRestrictShow): what the building sees in the
 * request, for how long, and the emergency path. The server needs a reason kind: an
 * emergency is a safety incident, anything else is filed against its support ticket (the
 * design asks a ticket for every request).
 */
export function TeamAccessRequest({ client, orgId, residentId, buildingName, residentName, onSent }: { client: ApiClient; orgId: string | null; residentId: string | null; buildingName?: string | null; residentName?: string | null; onSent?: () => void }) {
  const [open, setOpen] = useState(false);
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState(1);
  const [emergency, setEmergency] = useState(false);
  const [withResident, setWithResident] = useState(!!residentId);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [mine, setMine] = useState<TeamAccessRequestRow | null>(null);
  const bldg = buildingName || 'This building';

  // My latest request for this building: pending or turned down changes what the card says.
  useEffect(() => {
    if (!orgId) return;
    let live = true;
    teamAccessRequests(client, {})
      .then((r) => {
        if (!live) return;
        const rows = r.requests.filter((x) => x.organizationId === orgId).sort((a, b) => Date.parse(b.requestedAt ?? '') - Date.parse(a.requestedAt ?? ''));
        setMine(rows[0] ?? null);
      })
      .catch(() => { if (live) setMine(null); });
    return () => { live = false; };
  }, [client, orgId]);

  const ok = reason.trim().length > 5 && !!ticket.trim();
  const send = async () => {
    if (!orgId || !ok || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const stepUpToken = await passkeyStepUp(client);
      const r = await teamAccessRequest(client, {
        body: {
          organizationId: orgId,
          residentId: withResident ? residentId : null,
          reasonCode: emergency ? 'safety_incident' : 'support_ticket',
          ticket: ticket.trim(),
          details: reason.trim(),
          hours,
          emergency,
          stepUpToken,
        },
      });
      setMine(r.request);
      setOpen(false);
      onSent?.();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'The request could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const pending = mine?.status === 'pending';
  const denied = mine?.status === 'denied';
  const title = pending ? `Waiting for ${bldg} to approve` : denied ? `${bldg} turned down your request` : 'Restricted: request access';
  const lead = pending
    ? `Sent ${mine?.requestedAt ? hm(mine.requestedAt) : ''} for ${mine?.hours ?? 1}h: “${mine?.details ?? mine?.reason ?? ''}”. The facility admin sees it now and can approve or deny. Nothing here opens until they do.`
    : denied
      ? 'The facility admin denied it. Ask the building directly, or use the emergency path if a resident is at risk right now.'
      : 'Aithora staff don’t see resident records by default. Ask the building for access for a set time, with a reason and a ticket. Every record you open is logged for them.';
  const note = emergency
    ? `Access starts at once. ${bldg} is told now and reviews it within 1 business day. Use this only when waiting could hurt someone.`
    : 'The facility admin approves or denies. Access ends on its own after the time you pick.';
  const submitStyle = C + (ok && !busy ? (emergency ? 'background:#B3261E;color:#fff;' : 'background:#264C7E;color:#fff;') : 'background:rgba(34,39,46,.08);color:#54606C;pointer-events:none;');

  return (
    <div data-screen-label="Restricted · request access" data-testid="team-access-restricted" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:10px;max-width:760px;padding:18px 20px;margin-bottom:12px;border-radius:12px;background:#FFFFFF;border:1px solid rgba(34,39,46,.16);')}>
      <div style={sx('display:flex;align-items:center;gap:8px;')}>
        <span style={sx('font-size:14px;font-weight:800;letter-spacing:.1em;padding:3px 7px;border-radius:4px;background:rgba(34,39,46,.08);color:#3C454F;')}>RESTRICTED</span>
        <span style={sx('font-size:16px;color:#54606C;')}>{`${bldg} · resident records`}</span>
      </div>
      <div style={sx('font-size:19px;font-weight:700;letter-spacing:-.01em;color:#22272E;')}>{title}</div>
      <div style={sx('font-size:16px;color:#3C454F;line-height:1.5;max-width:70ch;text-wrap:pretty;')}>{lead}</div>
      {open && (
        <div style={sx('display:flex;flex-direction:column;gap:10px;padding-top:6px;border-top:1px solid rgba(34,39,46,.1);')}>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px 16px;')}>
            <label style={sx(LABEL)}>
              Building
              <select value={bldg} disabled style={sx(FIELD)}>
                <option value={bldg}>{bldg}</option>
              </select>
            </label>
            <label style={sx(LABEL)}>
              Resident, if known
              <select value={withResident ? 'r' : ''} onChange={(e) => setWithResident(e.target.value === 'r')} style={sx(FIELD)}>
                <option value="">Not known yet</option>
                {residentId && <option value="r">{residentName || 'This resident'}</option>}
              </select>
            </label>
            <label style={sx(LABEL)}>
              Ticket
              <input type="text" value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="e.g. SUP-1182" style={sx(FIELD)} />
            </label>
          </div>
          <label style={sx(LABEL)}>
            Reason
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What you need to see, and why" style={sx(FIELD)} />
          </label>
          <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
            <span style={sx('font-size:16px;font-weight:600;color:#3C454F;margin-right:4px;')}>For</span>
            {HOURS.map((h) => (
              <Btn key={h} s={C + (hours === h ? 'background:#22272E;color:#fff;' : 'color:#3C454F;border:1px solid rgba(34,39,46,.2);')} onClick={() => setHours(h)}>
                {h + (h === 1 ? ' hour' : ' hours')}
              </Btn>
            ))}
            <span style={sx('flex:1 1 auto;')} />
            <Btn s={C + (emergency ? 'background:#B3261E;color:#fff;' : 'color:#8F362F;border:1px solid rgba(179,38,30,.35);')} onClick={() => setEmergency((v) => !v)}>
              {emergency ? '✓ Emergency: a resident is at risk now' : 'Emergency?'}
            </Btn>
          </div>
          <div style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>{note}</div>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
            <Btn s={submitStyle} onClick={() => void send()} disabled={!ok || busy}>
              {busy ? 'Confirm with your passkey' : emergency ? 'Start emergency access' : 'Send request'}
            </Btn>
            <Btn s="display:inline-flex;align-items:center;height:34px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#3C454F;border:1px solid rgba(34,39,46,.2);" onClick={() => setOpen(false)}>
              Cancel
            </Btn>
          </div>
        </div>
      )}
      {!open && !pending && (
        <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
          <Btn s="display:inline-flex;align-items:center;height:34px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#264C7E;color:#fff;" onClick={() => setOpen(true)} disabled={!orgId}>
            Request access
          </Btn>
        </div>
      )}
      {msg && <div role="alert" style={sx('font-size:16px;color:#B3261E;')}>{msg}</div>}
    </div>
  );
}

/**
 * "Break-glass request" for the building's Facility Admin (design bgFaShow): Aithora's
 * request waiting on them, or an emergency start to review. Nothing shows when there is none.
 */
export function BreakGlassRequestCard({ client, onSeeLog }: { client: ApiClient; onSeeLog?: () => void }) {
  const [rows, setRows] = useState<TeamAccessRequestRow[] | null>(null);
  const [tick, setTick] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    teamAccessRequests(client, {})
      .then((r) => { if (live) setRows(r.requests); })
      .catch(() => { if (live) setRows(null); });
    return () => { live = false; };
  }, [client, tick]);
  const pend = (rows ?? []).find((r) => r.status === 'pending');
  const rev = (rows ?? []).find((r) => r.emergency && !r.reviewedAt && (r.status === 'approved' || r.status === 'ended' || r.status === 'expired'));
  const x = pend ?? rev;
  if (!x) return null;
  const isPend = x === pend;
  const act = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
      setTick((t) => t + 1);
    } catch (e) {
      setErr((e as Error).message || 'That did not go through. Try again.');
    }
  };
  const who = x.requesterName ?? 'Aithora';
  const facts: [string, string][] = [['Reason', x.details ?? x.reason], ['Ticket', x.ticket ?? ''], ['Resident', x.residentId ? 'one resident' : 'not known yet'], ['For', x.hours + 'h']];
  return (
    <div data-screen-label="Break-glass request" data-testid="break-glass-request" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:8px;padding:14px 16px;margin-bottom:12px;border-radius:12px;background:#FFFFFF;border:1px solid rgba(138,90,0,.45);box-shadow:inset 4px 0 0 #8A5A00;')}>
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;color:#8A5A00;')}>{isPend ? 'Aithora is asking to see resident records' : 'Emergency access to review'}</span>
        <span style={sx('flex:1 1 auto;')} />
        <span style={sx('font-size:16px;color:#54606C;')}>{x.requestedAt ? hm(x.requestedAt) : ''}</span>
      </div>
      <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{who + (isPend ? ` wants ${x.hours}h of access` : ' opened records under emergency access')}</div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:6px 18px;')}>
        {facts.map(([k, v]) => (
          <div key={k} style={sx('font-size:16px;line-height:1.4;')}>
            <span style={sx('color:#54606C;')}>{k}</span> &middot; <b>{v}</b>
          </div>
        ))}
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>
        {isPend
          ? `If you approve, they can see ${x.residentId ? 'that resident’s record' : 'resident records here'} until the time runs out. Every record they open shows in Settings → Break-glass.`
          : 'Access started without your approval because they marked it an emergency. Review what they opened, within 1 business day.'}
      </div>
      <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
        {isPend ? (
          <>
            <Btn s={C + 'background:#264C7E;color:#fff;'} onClick={() => void act(() => teamAccessApprove(client, { params: { id: x.id } }))}>{`Approve ${x.hours}h`}</Btn>
            <Btn s={C + 'color:#8F362F;border:1px solid rgba(179,38,30,.35);'} onClick={() => void act(() => teamAccessDeny(client, { params: { id: x.id } }))}>Deny</Btn>
          </>
        ) : (
          <>
            <Btn s={C + 'background:#264C7E;color:#fff;'} onClick={() => void act(() => teamAccessReview(client, { params: { id: x.id }, body: {} }))}>Mark reviewed</Btn>
            {onSeeLog && <Btn s={C + 'color:#3C454F;border:1px solid rgba(34,39,46,.2);'} onClick={onSeeLog}>See the log</Btn>}
          </>
        )}
      </div>
      {err && <div role="alert" style={sx('font-size:16px;color:#B3261E;')}>{err}</div>}
    </div>
  );
}
